import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { BusinessEntity, ScheduleFLine, ScheduleFReport } from "@/api/types";
import "./ReportsScheduleF.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/**
 * The Schedule F worksheet — GET /api/demo/reports/schedule-f. ef-044-2.
 *
 * <p>
 * NOT PART OF THE Reports AREA TAB STRIP. `AreaTabs`/`REPORTS_TABS` (ADR 0009 §2) is a
 * closed, small set of tabs under one area; the Reports picker (ef-044-1) is where every
 * report — this one included — is found and opened from, so this screen carries an "All
 * reports" link back to the picker instead, same shape a report-detail screen takes rather
 * than an area tab.
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Show IS PRESSED. Unlike Labour.tsx/Hours.tsx (which load the
 * server's own default window on mount), a Schedule F figure is read by an owner who
 * chooses an entity and a tax year FIRST — fetching against a guessed year on mount would
 * show a number for a year nobody asked about. Only the entity list (for the picker
 * itself) loads eagerly.
 * </p>
 *
 * <p>
 * THE DISCLAIMER IS ALWAYS ON SCREEN, never gated behind the report or behind `Why` — a
 * worksheet a farmer might screenshot or print needs "not for submission to the IRS"
 * visible whether or not a report has been requested yet (`schedule-f.spec.ts`'s own
 * "before any data is requested" test).
 * </p>
 */
export function ReportsScheduleF() {
  const entityFieldId = useId();
  const taxYearFieldId = useId();

  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  const [entityId, setEntityId] = useState<string>("");
  const [taxYear, setTaxYear] = useState<string>(String(new Date().getFullYear()));

  const [report, setReport] = useState<ScheduleFReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .getEntities(controller.signal)
      .then((rows) => {
        setEntities(rows);
        // Seeded once, from the server's own list — never left blank when there is
        // exactly one entity to pick, the common single-entity farm this booth demo
        // itself models (db/seed/0002_seed_demo.sql).
        setEntityId((current) => current || (rows[0]?.id ?? ""));
      })
      .catch(() => setEntities([]));
    return () => controller.abort();
  }, []);

  const parsedTaxYear = /^\d{4}$/.test(taxYear.trim()) ? Number(taxYear) : null;
  const canSubmit = entityId !== "" && parsedTaxYear !== null && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit || parsedTaxYear === null) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getScheduleFReport(entityId, parsedTaxYear);
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the Schedule F worksheet."));
    }
  }

  return (
    <main className="schedule-f">
      <PageHeader
        title="Schedule F"
        subtitle="Cash-basis farm income and expenses, by IRS Schedule F line"
      />

      <a className="schedule-f__all-reports" href="/reports">
        ← All reports
      </a>

      <p className="schedule-f__disclaimer" role="note">
        <strong>Not for submission to the IRS.</strong> This is a worksheet built from
        this farm&rsquo;s own ledger — it is not a completed, filed Schedule F, and lines
        with no mapped account are never guessed at.
      </p>

      <section className="schedule-f__panel" aria-labelledby="schedule-f-heading" data-state={status}>
        <h2 id="schedule-f-heading" className="schedule-f__heading">
          Worksheet
        </h2>

        <form className="schedule-f__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="schedule-f__field">
            <label htmlFor={entityFieldId}>Entity</label>
            <select
              id={entityFieldId}
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              disabled={entities === null || entities.length === 0}
            >
              {entities === null ? (
                <option value="">Loading…</option>
              ) : entities.length === 0 ? (
                <option value="">No entities on this farm yet</option>
              ) : (
                entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="schedule-f__field">
            <label htmlFor={taxYearFieldId}>Tax year</label>
            <input
              id={taxYearFieldId}
              type="number"
              inputMode="numeric"
              step="1"
              value={taxYear}
              onChange={(event) => setTaxYear(event.target.value)}
              aria-invalid={taxYear !== "" && parsedTaxYear === null ? true : undefined}
            />
          </div>

          <button type="submit" className="btn btn--primary schedule-f__show" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Show"}
          </button>
        </form>

        <Why>
          Lines are grouped by <code>account.schedule_f_line</code> — the real IRS line a
          farm has mapped each account of its own chart onto. An account with no mapping
          yet shows its total separately, under Unmapped, rather than being folded into a
          line it was never assigned to.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the Schedule F worksheet."
            detail={error ?? undefined}
          />
        )}

        {status === "ready" && report !== null && (
          <>
            <div className="schedule-f__summary">
              <div className="schedule-f__summary-line">
                <span>Gross income</span>
                <span className="schedule-f__gross">{formatMoney(report.grossIncome)}</span>
              </div>
              <div className="schedule-f__summary-line">
                <span>Total expenses</span>
                <span className="schedule-f__total">{formatMoney(report.totalExpenses)}</span>
              </div>
              <div className="schedule-f__summary-line schedule-f__summary-line--net">
                <span>Net farm profit</span>
                <span className="schedule-f__net">{formatMoney(report.netFarmProfit)}</span>
              </div>
            </div>

            <ScheduleFTable title="Income" lines={report.incomeLines} />
            <ScheduleFTable title="Expenses" lines={report.expenseLines} />

            {report.unmappedTotal !== 0 && (
              <p className="schedule-f__unmapped" role="status">
                <strong>{formatMoney(report.unmappedTotal)}</strong> in activity has no
                Schedule F line mapped yet and is not included in the totals above.
              </p>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function ScheduleFTable({ title, lines }: { title: string; lines: ScheduleFLine[] }) {
  return (
    <div className="schedule-f__table-wrap" tabIndex={0} role="group" aria-label={title}>
      <h3 className="schedule-f__table-heading">{title}</h3>
      <table className="schedule-f__table ruled-rows">
        <thead>
          <tr>
            <th scope="col">Line</th>
            <th scope="col">Description</th>
            <th scope="col" className="schedule-f__num">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={3} className="schedule-f__empty-row">
                Nothing mapped to a {title.toLowerCase()} line yet.
              </td>
            </tr>
          ) : (
            lines.map((line) => (
              <tr key={line.line}>
                <td className="schedule-f__line-number">{line.line}</td>
                <th scope="row" className="schedule-f__line-label">
                  {line.label ?? "—"}
                </th>
                <td className="schedule-f__num schedule-f__total">{formatMoney(line.total)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
